'use client';

import { useEffect, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import { useTenantOptional } from '@/contexts/TenantContext';
import { fireRREvent } from '@/components/analytics/RouteRoyalTracker';

/**
 * UserIdentitySync
 *
 * Listens for Supabase auth state changes and syncs the user identity
 * to GA4 and the GTM dataLayer. Also initialises Meta Advanced Matching
 * when a Facebook Pixel is configured and user data is available.
 *
 * This enables:
 *  - Cross-device tracking in GA4
 *  - CRM-to-analytics matching
 *  - Audience building based on logged-in status
 *  - Meta Advanced Matching for improved ad attribution
 */
export function UserIdentitySync() {
  const lastSyncedId = useRef<string | null>(null);
  const tenant = useTenantOptional();
  const facebookPixelId = tenant?.analytics?.keys?.facebookPixel;

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Check for an existing session immediately
    const stored = getSupabaseUserFromStorage();
    if (stored?.id && stored.id !== lastSyncedId.current) {
      syncIdentity(stored.id, facebookPixelId, stored.email);
      lastSyncedId.current = stored.id;
    }

    // Subscribe to auth state changes for real-time updates
    const supabase = createClient();
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        const id = session?.user?.id ?? null;
        const email = session?.user?.email;

        if (id && id !== lastSyncedId.current) {
          syncIdentity(id, facebookPixelId, email);
          lastSyncedId.current = id;
        } else if (!id && lastSyncedId.current) {
          // User signed out
          lastSyncedId.current = null;
          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'user_signed_out',
            is_authenticated: false,
          });
        }
      },
    );

    return () => {
      subscription.unsubscribe();
    };
  }, [facebookPixelId]);

  return null;
}

/**
 * Extract the Supabase user ID and email from the auth token stored in
 * localStorage. Used for the initial check before the subscription is active.
 */
function getSupabaseUserFromStorage(): { id: string; email?: string } | null {
  if (typeof window === 'undefined') return null;

  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key);
        if (raw) {
          const parsed = JSON.parse(raw);
          const userId = parsed?.user?.id;
          if (userId) {
            const email = parsed?.user?.email;
            return email ? { id: userId, email } : { id: userId };
          }
        }
      }
    }
  } catch {
    // Storage access failed
  }

  return null;
}

/**
 * Sync the user identity across GA4, the dataLayer, and Meta Advanced Matching.
 */
function syncIdentity(
  userId: string,
  facebookPixelId?: string,
  email?: string,
) {
  if (typeof window === 'undefined') return;

  // GA4 user_id
  if (window.gtag) {
    window.gtag('set', 'user_id', userId);
  }

  // GTM dataLayer
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: 'user_identified',
    user_id: userId,
    is_authenticated: true,
  });

  // Meta Advanced Matching — re-init the pixel with hashed user data
  if (facebookPixelId && email && window.fbq) {
    window.fbq('init', facebookPixelId, { em: email.trim().toLowerCase() });
  }

  // First-party identity stitching — link the anonymous visitor to their
  // Route Royal contact so prior browsing attributes to the individual.
  // The backend resolves the contact by email and backfills past events.
  if (email) {
    fireRREvent('identify', { email: email.trim().toLowerCase() });
  }
}

declare global {
  interface Window {
    gtag?: (...args: [string, ...unknown[]]) => void;
    fbq?: (command: string, event: string, params?: Record<string, unknown>) => void;
    dataLayer?: unknown[];
  }
}
